import { canonicalJson, sha256Hex } from '../utils/audit-crypto.js';
export class LineageError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
const object = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const invalid = (message) => { throw new LineageError('invalid_lineage', message); };
const keys = (v, allowed) => {
    if (Object.keys(v).some(k => !allowed.includes(k)))
        invalid('Unexpected field in lineage graph');
};
/** Bounded DAG of metadata only; no raw records, credentials or external fetches. */
export function validateLineageGraph(value) {
    if (!object(value))
        return invalid('Graph must be an object');
    keys(value, ['nodes', 'edges']);
    if (!Array.isArray(value.nodes) || value.nodes.length > 200 || !Array.isArray(value.edges) || value.edges.length > 500)
        return invalid('Graph requires at most 200 nodes and 500 edges');
    const ids = new Set();
    const nodes = value.nodes.map(n => {
        if (!object(n))
            return invalid('Node must be an object');
        keys(n, ['id', 'name', 'type', 'pii_categories', 'retention_days']);
        if (typeof n.id !== 'string' || !/^[a-zA-Z0-9_-]{1,64}$/.test(n.id) || ids.has(n.id))
            return invalid('Node IDs must be unique');
        ids.add(n.id);
        if (typeof n.name !== 'string' || !n.name.trim() || n.name.length > 200)
            return invalid('Node name is required (at most 200 characters)');
        if (!['dataset', 'transform', 'model'].includes(n.type))
            return invalid('Node type must be dataset, transform or model');
        if (!Array.isArray(n.pii_categories) || n.pii_categories.length > 20 || n.pii_categories.some(p => typeof p !== 'string' || !p.trim() || p.length > 100))
            return invalid('PII categories must be a list of short names');
        if (n.retention_days !== null && (!Number.isSafeInteger(n.retention_days) || n.retention_days < 0 || n.retention_days > 36500))
            return invalid('Retention must be null or 0–36500 days');
        return { id: n.id, name: n.name.trim(), type: n.type, pii_categories: [...new Set(n.pii_categories)], retention_days: n.retention_days };
    });
    const seen = new Set();
    const edges = value.edges.map(e => {
        if (!object(e))
            return invalid('Edge must be an object');
        keys(e, ['from', 'to']);
        if (typeof e.from !== 'string' || typeof e.to !== 'string' || !ids.has(e.from) || !ids.has(e.to) || e.from === e.to)
            return invalid('Edges must connect distinct existing nodes');
        const key = `${e.from}:${e.to}`;
        if (seen.has(key))
            return invalid('Duplicate edge');
        seen.add(key);
        return { from: e.from, to: e.to };
    });
    const incoming = new Map(nodes.map(n => [n.id, 0]));
    for (const e of edges)
        incoming.set(e.to, incoming.get(e.to) + 1);
    const ready = nodes.filter(n => incoming.get(n.id) === 0).map(n => n.id);
    let visited = 0;
    while (ready.length) {
        const id = ready.pop();
        visited++;
        for (const e of edges.filter(e => e.from === id)) {
            incoming.set(e.to, incoming.get(e.to) - 1);
            if (incoming.get(e.to) === 0)
                ready.push(e.to);
        }
    }
    if (visited !== nodes.length)
        return invalid('Lineage cannot contain cycles');
    return { nodes, edges };
}
async function requireSystem(db, workspaceId, systemId) {
    const result = await db.query('SELECT system_id FROM ai_systems WHERE system_id = ? AND workspace_id = ?', [systemId, workspaceId]);
    if (!result.results.length)
        throw new LineageError('not_found', 'System not found');
}
export async function getDataLineage(db, workspaceId, systemId) {
    await requireSystem(db, workspaceId, systemId);
    const rows = await db.query('SELECT graph_json, revision, updated_at FROM system_data_lineage WHERE workspace_id = ? AND system_id = ?', [workspaceId, systemId]);
    const row = rows.results[0];
    return row ? { graph: validateLineageGraph(JSON.parse(row.graph_json)), revision: row.revision, updated_at: row.updated_at } : { graph: { nodes: [], edges: [] }, revision: 0, updated_at: null };
}
/** Full graph replacement with optimistic concurrency; removing nodes/edges is deletion. */
export async function saveDataLineage(db, workspaceId, systemId, userId, graph, expectedRevision) {
    await requireSystem(db, workspaceId, systemId);
    if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0)
        return invalid('Expected revision must be a non-negative integer');
    const validated = validateLineageGraph(graph);
    const json = canonicalJson(validated);
    if (new TextEncoder().encode(json).length > 100_000)
        return invalid('Graph exceeds 100 KB');
    const now = new Date().toISOString();
    const result = expectedRevision === 0
        ? await db.execute('INSERT INTO system_data_lineage (system_id,workspace_id,graph_json,revision,updated_by,updated_at) VALUES (?,?,?,1,?,?) ON CONFLICT (system_id) DO NOTHING', [systemId, workspaceId, json, userId, now])
        : await db.execute('UPDATE system_data_lineage SET graph_json = ?, revision = revision + 1, updated_by = ?, updated_at = ? WHERE system_id = ? AND workspace_id = ? AND revision = ?', [json, userId, now, systemId, workspaceId, expectedRevision]);
    if (result.meta?.changes !== 1)
        throw new LineageError('revision_conflict', 'The graph changed. Reload before saving.');
    return { graph: validated, revision: expectedRevision + 1, updated_at: now };
}
export function lineageDisclosureFields(graph) {
    const validated = validateLineageGraph(graph);
    return {
        data_lineage: validated,
        data_sources: validated.nodes.filter(n => n.type === 'dataset').map(n => ({ id: n.id, name: n.name, pii_categories: n.pii_categories, retention_days: n.retention_days })),
    };
}
// ============================================================
// Lineage-specific drift (V85-5-9 / gap I10) — the current draft's
// canonical hash vs. the hash of the graph last embedded in a
// published disclosure. Used by EvidenceDriftService; kept here so
// the hashing/lookup logic lives next to the graph shape it hashes.
// ============================================================
/** Deterministic hash of a validated lineage graph. */
export async function hashLineageGraph(graph) {
    return sha256Hex(canonicalJson(validateLineageGraph(graph)));
}
/**
 * The most recently published disclosure version for this system that
 * embeds a `data_lineage` field (via `lineageDisclosureFields`), plus
 * its hash. Returns null when the system has no disclosure, or its
 * latest published version predates lineage (no `data_lineage` key).
 */
export async function getPublishedLineageSnapshot(db, workspaceId, systemId) {
    const rows = await db.query(`SELECT dv.version_id, dv.content_json, dv.published_at
       FROM ai_disclosure_versions dv
       JOIN ai_disclosures d ON d.disclosure_id = dv.disclosure_id
      WHERE d.workspace_id = ? AND d.system_id = ? AND dv.version_id = d.current_version_id
      ORDER BY dv.version_no DESC LIMIT 1`, [workspaceId, systemId]);
    const row = rows.results[0];
    if (!row)
        return null;
    let content;
    try {
        content = JSON.parse(row.content_json);
    }
    catch {
        return null;
    }
    if (content.data_lineage == null)
        return null;
    let graph;
    try {
        graph = validateLineageGraph(content.data_lineage);
    }
    catch {
        return null;
    }
    return {
        graph,
        hash: await hashLineageGraph(graph),
        version_id: row.version_id,
        published_at: row.published_at,
    };
}
/**
 * Compares the current draft graph to the last published snapshot.
 * Returns null when there is nothing to compare (no draft, no
 * publication, or the two hashes match).
 */
export async function computeLineageDrift(db, workspaceId, systemId) {
    const draft = await getDataLineage(db, workspaceId, systemId);
    if (draft.revision === 0)
        return null; // no draft saved yet
    const published = await getPublishedLineageSnapshot(db, workspaceId, systemId);
    if (!published)
        return null;
    const currentHash = await hashLineageGraph(draft.graph);
    if (currentHash === published.hash)
        return null;
    return { current_hash: currentHash, published_hash: published.hash, published_version_id: published.version_id };
}
export function scoreDataGovernanceFit(graph) {
    const datasets = graph.nodes.filter((n) => n.type === 'dataset');
    if (datasets.length === 0) {
        return { fit: 'gap', confidence: 0, rationale: 'No dataset nodes in the lineage graph' };
    }
    const withPii = datasets.filter((n) => n.pii_categories.length > 0).length;
    const withRetention = datasets.filter((n) => n.retention_days !== null).length;
    const piiCoverage = withPii / datasets.length;
    const retentionCoverage = withRetention / datasets.length;
    const connectedNodeIds = new Set();
    for (const e of graph.edges) {
        connectedNodeIds.add(e.from);
        connectedNodeIds.add(e.to);
    }
    const connectedDatasets = datasets.filter((n) => connectedNodeIds.has(n.id)).length;
    const connectivity = connectedDatasets / datasets.length;
    // Weighted blend: classification + retention carry the most signal
    // for "is this a real data-governance answer", connectivity is a
    // smaller corroborating signal that the graph traces an actual flow.
    const confidence = Math.round((piiCoverage * 0.45 + retentionCoverage * 0.35 + connectivity * 0.2) * 100) / 100;
    const fit = confidence >= 0.75 ? 'native' : confidence >= 0.3 ? 'substitute' : 'gap';
    return {
        fit,
        confidence,
        rationale: `${datasets.length} dataset node(s): ${Math.round(piiCoverage * 100)}% PII-categorized, ` +
            `${Math.round(retentionCoverage * 100)}% with a retention period, ${Math.round(connectivity * 100)}% connected to the graph`,
    };
}
//# sourceMappingURL=data-lineage-service.js.map